// WG-3.1 Smart Search chip — composer web-access mode selector.
//
// Sits in the chat composer bottom bar AFTER the model selector:
//   +   ⚙ Ornith 35B   🌐 Умный поиск            ↑
//
// Three modes (mapped 1:1 onto the canonical domain WebMode):
//   Всегда    → web.mode=on    (search every turn) — product DEFAULT (Owner 21-10)
//   Авто      → web.mode=auto  (capability available; router decides)
//   Выключено → web.mode=off   (never search)
//
// Visual language (Owner brand tokens, 21-10 polish): the main chip label
// is CONSTANT («Умный поиск») regardless of mode — the mode is conveyed by
// the dropdown selection, the aria-label, and the chip's calm active/muted
// style, NEVER by a loud fill. ALWAYS and AUTO share the SAME calm visual
// weight (subtle cyan tint + cyan icon); the old filled-cyan ALWAYS style
// was removed (Owner: «слишком ярко, режет глаз»). OFF is muted but stays
// interactive (not disabled semantics).
//
// Persistence: per-user server preference (user_preferences key
// web_search_mode) — not localStorage, not per-chat. Rationale (WG-3.1
// §28): the canonical preference authority in this architecture is the
// user_preferences allowlist (services/docs/preferences.py); a chat-level
// reset would fight the user's explicit choice, and localStorage would be
// lost on device change and bypass the server contract. The default (no
// stored row) is ALWAYS; an explicit stored AUTO/OFF is never migrated.
import { useEffect, useRef, useState } from "react";
import { Globe, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tip } from "@/components/Tip";
import type { WebSearchMode } from "@/lib/api";

interface SmartSearchChipProps {
  mode: WebSearchMode;
  onModeChange: (mode: WebSearchMode) => void;
  // Compact live status while a search-backed turn streams (§29).
  // WG-4B §56: "reading" = a URL in the message opened the Reader.
  searchStatus?: "searching" | "reading" | "found" | "degraded" | null;
  searchCount?: number;
}

/** Constant main-chip label — the mode is NOT shown in the composer (§7). */
const CHIP_LABEL = "Умный поиск";

/** Accessible mode name for aria-label + menu. */
const MODE_NAME: Record<WebSearchMode, string> = {
  on: "Всегда",
  auto: "Авто",
  off: "Выключено",
};

// Default-first menu order (Owner 21-10): Всегда → Авто → Выключено.
const MODE_OPTIONS: { mode: WebSearchMode; title: string; desc: string }[] = [
  { mode: "on", title: "Всегда", desc: "Использовать интернет для каждого запроса" },
  { mode: "auto", title: "Авто", desc: "Искать только когда это необходимо" },
  { mode: "off", title: "Выключено", desc: "Не обращаться к интернету" },
];

export function SmartSearchChip({
  mode,
  onModeChange,
  searchStatus = null,
  searchCount,
}: SmartSearchChipProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Keyboard: Escape closes the menu.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Neutral tooltip — the old «при необходимости» wording is wrong now
  // that the default is ALWAYS (§15).
  const tooltip = "Настройки поиска в интернете";

  return (
    <div className="relative" ref={ref}>
      <Tip label={tooltip}>
        <button
          type="button"
          data-testid="smart-search-chip"
          data-mode={mode}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`${CHIP_LABEL}: ${MODE_NAME[mode]}`}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "flex items-center gap-1.5 px-2 h-8 rounded-lg text-sm transition-colors",
            // ALWAYS and AUTO share ONE calm active style (§9–§11): subtle
            // cyan tint, cyan icon, no fill, no glow, no font emphasis.
            (mode === "auto" || mode === "on") &&
              "smart-search-active text-[#22D3EE] bg-[#22D3EE]/10 hover:bg-[#22D3EE]/15",
            // OFF: muted but fully interactive (§13).
            mode === "off" &&
              "smart-search-muted text-tertiary hover:text-secondary hover:bg-hoverbg",
          )}
        >
          <Globe size={16} className="shrink-0" />
          <span className="hidden sm:inline truncate">{CHIP_LABEL}</span>
          {searchStatus === "searching" && (
            <span
              className="hidden md:inline text-[11px] text-[#22D3EE] animate-pulse"
              data-testid="smart-search-status"
            >
              Ищу в интернете…
            </span>
          )}
          {searchStatus === "reading" && (
            <span
              className="hidden md:inline text-[11px] text-[#22D3EE] animate-pulse"
              data-testid="smart-search-status"
            >
              Читаю страницу…
            </span>
          )}
          {searchStatus === "found" && (
            <span className="hidden md:inline text-[11px] text-[#22D3EE]" data-testid="smart-search-status">
              Использовано {searchCount ?? 0} {plural(searchCount ?? 0)}
            </span>
          )}
          {searchStatus === "degraded" && (
            <span className="hidden md:inline text-[11px] text-amber-400" data-testid="smart-search-status">
              Поиск недоступен
            </span>
          )}
        </button>
      </Tip>
      {open && (
        <div
          role="menu"
          className="absolute left-0 bottom-full mb-2 w-72 max-w-[calc(100vw-64px)] bg-elevated border border-strongline rounded-lg shadow-lg overflow-hidden z-30"
        >
          <div className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-wide text-tertiary">
            Интернет
          </div>
          {MODE_OPTIONS.map((opt) => (
            <button
              key={opt.mode}
              role="menuitemradio"
              aria-checked={mode === opt.mode}
              data-testid={`smart-search-option-${opt.mode}`}
              onClick={() => {
                onModeChange(opt.mode);
                setOpen(false);
              }}
              className={cn(
                "w-full text-left px-4 py-2 hover:bg-hoverbg flex items-start gap-2",
                mode === opt.mode ? "text-[#22D3EE]" : "text-primary",
              )}
            >
              <span className="mt-0.5 shrink-0 w-4 h-4 flex items-center justify-center">
                {mode === opt.mode ? (
                  <Check size={14} />
                ) : (
                  <span className="w-2 h-2 rounded-full border border-current opacity-50" />
                )}
              </span>
              <span>
                <span className="block text-sm">{opt.title}</span>
                <span className="block text-xs text-tertiary">{opt.desc}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function plural(n: number): string {
  if (n % 10 === 1 && n % 100 !== 11) return "источник";
  if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) return "источника";
  return "источников";
}
